import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { FormSpy } from "react-final-form";

import { Button, Typography } from "@material-ui/core";

import Row from "components/Form/Row";
import Search from "components/Input/Search";
import FormControl from "components/FormControl";
import LoadingButton from "components/LoadingButton";
import SubmitButtonControl from "components/FormControl/SubmitButtonControl";
import CardListFieldControl from "components/FormControl/CardListFieldControl";
import Loader from "components/Layout/Loader";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import {
  RestoreTableStoreType,
  TableSortType,
} from "stores/templates/TableStore";

import {
  Container,
  Buttons,
  ButtonContainer,
  SearchControlWrapper,
  SearchControls,
  TitleContainer,
  SubtitleContainer,
} from "./styles";

type FormSubmitData = {
  values: any;
};

type Props = {
  isLoading: boolean;
  store: RestoreTableStoreType;
  placeholderText: string;
  withFilter?: boolean;
  withAccessProgramFilter?: boolean;
  onClose: () => void;
  onChangeIsDirty: (params: { dirty: boolean }) => void;
  onSubmit: (data: FormSubmitData) => void;
} & SnackbarContextProps;

const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class RestoreArchivedForm extends React.Component<Props> {
  get store() {
    const { store } = this.props;
    return store;
  }

  @computed get hasMoreInfo() {
    return this.store?.hasMoreInfo;
  }

  @computed get searchValue() {
    return this.store?.search;
  }

  @computed get options() {
    return this.store?.options;
  }

  componentDidMount() {
    this.store?.fetch();
  }

  componentWillUnmount() {
    this.store?.reset();
  }

  fetchMore = async () => {
    const { setMessage } = this.props;

    try {
      await this.store?.fetchMore();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    this.store?.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && this.store?.fetch();
  };

  onSearchClear = () => {
    this.store?.clearSearch();
    this.store?.fetch();
  };

  onSortApply = (value: TableSortType) => {
    this.store?.applyOrder(value);
    this.store?.resetPage();
    this.store?.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      this.store?.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    this.store?.resetPage();
    this.store?.fetch();
  };

  onFilterClear = () => {
    this.store?.clearFilters();
    this.store?.fetch();
  };

  render() {
    const {
      onClose,
      onChangeIsDirty,
      onSubmit,
      placeholderText,
      withFilter,
      withAccessProgramFilter,
    } = this.props;

    return (
      <FormControl onSubmit={onSubmit} initialValues={{}}>
        <FormSpy subscription={{ dirty: true }} onChange={onChangeIsDirty} />
        <Container>
          <FormSpy subscription={{ values: true }}>
            {({ values }) => {
              const selectedItemsCount = values?.ids?.length;

              return (
                <Row>
                  <TitleContainer>
                    <Typography variant="h1" color="textPrimary">
                      Restore from Archive
                    </Typography>
                    {!!selectedItemsCount && (
                      <SubtitleContainer>
                        <Typography variant="subtitle1" color="textPrimary">
                          {`${selectedItemsCount} items selected`}
                        </Typography>
                      </SubtitleContainer>
                    )}
                  </TitleContainer>
                </Row>
              );
            }}
          </FormSpy>

          <Row>
            <Search
              initValue={this.searchValue}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={this.store?.orderContext.appliedOrderInfo}
                  onApply={this.onSortApply}
                  disablePortal
                />
              </SearchControlWrapper>
              {withFilter && (
                <SearchControlWrapper>
                  <FilteringControl
                    initValue={this.store?.filters}
                    onApply={this.onFilterApply}
                    onClear={this.onFilterClear}
                    statusFiltering
                    accessProgramsFiltering={withAccessProgramFilter}
                  />
                </SearchControlWrapper>
              )}
            </SearchControls>
          </Row>

          <Row>
            {!this.store?.isLoading ? (
              <CardListFieldControl
                name={"ids"}
                options={this.options}
                hasMore={this.hasMoreInfo}
                height={"450px"}
                fetchMore={this.fetchMore}
                searchValue={this.searchValue}
                noItemsPlaceholderText={placeholderText}
                multi
                withInfinityScroll
              />
            ) : (
              <Loader />
            )}
          </Row>
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <SubmitButtonControl render={this.renderButton} />
          </ButtonContainer>
        </Buttons>
      </FormControl>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={this.props.isLoading}
      fullWidth
    >
      Restore
    </LoadingButton>
  );
}

export default withGlobalSnackbar(RestoreArchivedForm);
